import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HiArrowLeft, HiChatAlt2, HiShare, HiX } from "react-icons/hi";
import ConversationBrowser from "../../components/chat/ConversationBrowser";
import ConversationView from "../../components/chat/ConversationView";
import { useChat } from "../../components/chat/ChatProvider";
import { useIsMobile } from "../../components/chat/useMedia";
import { useFullScreenView } from "../../utils/viewport";
import { useBackToClose } from "../../utils/backToClose";
import { takeSharedPayload } from "../../components/chat/shareInbox";

// "Send to…" bar while something shared from another app waits for a conversation to be picked.
function ShareBar({ payload, onCancel }) {
  const count = payload.files.length;
  const summary = [count ? `${count} file${count === 1 ? "" : "s"}` : "", payload.text ? `“${payload.text.slice(0, 60)}${payload.text.length > 60 ? "…" : ""}”` : ""]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex items-center gap-3 mx-2 mb-1 rounded-xl bg-blue-50 dark:bg-blue-500/10 px-3 py-2.5 shrink-0" role="status">
      <HiShare className="w-5 h-5 shrink-0 text-[#2470B7] dark:text-blue-300" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Choose a chat to send to</p>
        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{summary}</p>
      </div>
      <button type="button" onClick={onCancel} className="p-1.5 rounded-full text-gray-500 hover:bg-black/5 dark:hover:bg-white/10" aria-label="Cancel sharing">
        <HiX className="w-5 h-5" />
      </button>
    </div>
  );
}

// Full-page chat: conversation list on the left, the selected conversation on the right.
// On phones it's a full-screen, one-pane app like WhatsApp: the list, then the conversation with a back arrow.
// appBar: set by the standalone chat app (pages/chat/ChatApp.jsx). It takes the place of the "Leave chat"
// arrow, and the chat fills the whole window instead of sitting under the ERP header.
function ChatPage({ appBar = null, banner = null }) {
  const { setPageMode, pageConversationId, openConversation, conversations, sortedConversations } = useChat();
  const location = useLocation();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [showList, setShowList] = useState(false);
  const [shared, setShared] = useState(null); // shared from another app, waiting for a chat to be picked
  const [delivery, setDelivery] = useState(null); // { conversationId, payload } handed to that chat's composer
  // Phones: keep the chat header on screen when the keyboard opens.
  useFullScreenView(mobile);

  const stateConversationId = location.state?.conversationId;
  useEffect(() => {
    // ?c=<id> comes from a push-notification click when the app wasn't open.
    const fromQuery = Number(new URLSearchParams(location.search).get("c")) || undefined;
    setPageMode(true, stateConversationId ?? fromQuery);
    return () => setPageMode(false);
    // The id, not location.state: Back from an open chat restores an equal-but-new state object.
  }, [setPageMode, stateConversationId, location.search]);

  // ?share=1: the service worker received a share from another app (Android share sheet).
  const shareRequested = new URLSearchParams(location.search).get("share") === "1";
  useEffect(() => {
    if (!shareRequested) return;
    navigate(location.pathname, { replace: true });
    takeSharedPayload().then((payload) => {
      if (!payload) return;
      setShared(payload);
      setShowList(true);
    });
  }, [shareRequested, navigate, location.pathname]);

  const selectConversation = (id) => {
    openConversation(id);
    setShowList(false);
    if (shared) {
      setDelivery({ conversationId: id, payload: shared });
      setShared(null);
    }
  };

  const activeId = conversations[pageConversationId] ? pageConversationId : null;
  const shareFor = (id) => (delivery?.conversationId === id ? delivery.payload : undefined);
  // Phones: Back from a conversation returns to the chat list.
  useBackToClose(mobile && Boolean(activeId) && !showList, () => setShowList(true));

  // A newly opened conversation (from the list, a notification or a tour) is shown on phones too.
  useEffect(() => {
    if (activeId && !shared) setShowList(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // A tour about the conversation screen asks for a chat to be open; show the latest one.
  useEffect(() => {
    const onPrepare = (e) => {
      if (e.detail === "open-conversation" && !activeId && sortedConversations[0]) openConversation(sortedConversations[0].id);
    };
    window.addEventListener("irg:tour-prepare", onPrepare);
    return () => window.removeEventListener("irg:tour-prepare", onPrepare);
  }, [activeId, sortedConversations, openConversation]);

  if (mobile) {
    const conversationOpen = activeId && !showList;
    return (
      <div className="mobile-fullscreen z-[55] flex flex-col bg-white dark:bg-gray-900" data-chat-mobile>
        {conversationOpen ? (
          <ConversationView
            key={activeId}
            conversationId={activeId}
            variant="page"
            onBack={() => setShowList(true)}
            share={shareFor(activeId)}
            onShareUsed={() => setDelivery(null)}
          />
        ) : (
          <>
            {appBar || (
              <div className="flex items-center gap-2 px-2 pt-2 pb-1 shrink-0">
                <button type="button" onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Leave chat">
                  <HiArrowLeft className="w-5 h-5" />
                </button>
              </div>
            )}
            {banner}
            {shared && <ShareBar payload={shared} onCancel={() => setShared(null)} />}
            <div className="flex-1 min-h-0">
              <ConversationBrowser activeId={null} onSelect={selectConversation} />
            </div>
          </>
        )}
      </div>
    );
  }

  const desktop = (
    <div
      className={
        appBar
          ? "flex-1 min-h-0 flex border-t border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900"
          : "h-[calc(100vh-112px)] flex rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900"
      }
    >
      <aside className="w-80 shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        {shared && <div className="pt-2"><ShareBar payload={shared} onCancel={() => setShared(null)} /></div>}
        <div className="flex-1 min-h-0">
          <ConversationBrowser activeId={activeId} onSelect={selectConversation} />
        </div>
      </aside>
      <section className="flex-1 min-w-0">
        {activeId ? (
          <ConversationView
            key={activeId}
            conversationId={activeId}
            variant="page"
            share={shareFor(activeId)}
            onShareUsed={() => setDelivery(null)}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center text-gray-500 px-6">
            <HiChatAlt2 className="w-14 h-14 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-base font-medium text-gray-700 dark:text-gray-200">Select a conversation</p>
            <p className="text-sm">Pick a chat on the left, or start a new one with a person or a group.</p>
          </div>
        )}
      </section>
    </div>
  );

  if (!appBar) return desktop;
  return (
    <div className="h-[100dvh] flex flex-col bg-white dark:bg-gray-900">
      {appBar}
      {banner}
      {desktop}
    </div>
  );
}

export default ChatPage;
